import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { LineChart } from "@mui/x-charts/LineChart";
import { Suspense, useState } from "react";

import { useGetDeliveryProgressStatsQuery } from "@/libs/generated/hooks";
import { DeliveryType } from "@/types/models/delivery.types";

const chartColors: Partial<Record<DeliveryType, string>> & { default: string } = {
  [DeliveryType.STANDARD]: "#1a5da6",
  [DeliveryType.TO_RETURN]: "#d4a81b",
  [DeliveryType.TWO_WAY_DELIVERY]: "#8a4fce",
  [DeliveryType.OKM]: "#3da58a",
  default: "#6c757d",
};

const chartLabels: Record<string, string> = {
  [DeliveryType.STANDARD]: "Demandes standard",
  [DeliveryType.TO_RETURN]: "Demandes de retour",
  [DeliveryType.TWO_WAY_DELIVERY]: "Livraisons aller-retour",
  [DeliveryType.OKM]: "Demandes 0KM",
  default: "Toutes demandes",
};

export default function DeliveryProgressChartContainer() {
  const [deliveryType, setDeliveryType] = useState<DeliveryType | "">("");

  const handleChange = (event: SelectChangeEvent<DeliveryType | "">) => {
    setDeliveryType(event.target.value);
  };

  // Custom render function to display the selected value
  const renderValue = (selected: unknown) => {
    if (selected === "") return "Tous types";
    return typeof selected === "string" ? (chartLabels[selected] ?? selected) : "Tous types";
  };

  return (
    <Box>
      <Stack
        spacing={2}
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
          mb: 4,
        }}
      >
        <Typography variant="h6">Evolution des demandes</Typography>
        <FormControl
          sx={{
            width: 200,
          }}
        >
          <InputLabel id="chartFilterId" shrink={true}>
            Type
          </InputLabel>{" "}
          {/* Fixed label with shrink={true} */}
          <Select
            labelId="chartFilterId"
            id="select-delivery-type-for-chart"
            value={deliveryType}
            label="Type"
            onChange={handleChange}
            renderValue={renderValue}
            displayEmpty
          >
            <MenuItem value="">Tous types</MenuItem>
            <MenuItem value={DeliveryType.STANDARD}>Standard</MenuItem>
            <MenuItem value={DeliveryType.TO_RETURN}>To Return</MenuItem>
            <MenuItem value={DeliveryType.TWO_WAY_DELIVERY}>Two way delivery</MenuItem>
            <MenuItem value={DeliveryType.OKM}>0KM</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Suspense
        fallback={
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        }
      >
        <DeliveryProgressChart type={deliveryType} />
      </Suspense>
    </Box>
  );
}

export function DeliveryProgressChart({ type }: { type: DeliveryType | "" }) {
  const { data, isPending, error } = useGetDeliveryProgressStatsQuery(type ? { type } : {});

  if (isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Error loading chart data:
          {error instanceof Error ? error.message : "Unknown error"}
        </Typography>
      </Box>
    );
  }

  const _data = data?.getDeliveryProgressStats || [];

  const color = type ? (chartColors[type] ?? chartColors.default) : chartColors.default;
  const label = type ? (chartLabels[type] ?? chartLabels.default) : chartLabels.default;

  if (_data.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No data available for the selected type.</Typography>
      </Box>
    );
  }

  // Find the maximum y value in the data
  const maxYValue = Math.max(..._data.map((item) => item.y));

  // Double the max value for better visualization
  const yAxisMax = maxYValue * 2;

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <LineChart
        dataset={_data}
        height={340}
        series={[
          {
            dataKey: "y",
            label,
            baseline: 0,
            color,
            curve: "catmullRom",
            showMark: false,
          },
        ]}
        xAxis={[
          {
            dataKey: "x",
            scaleType: "band",
            valueFormatter: (value) => {
              const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ];
              return months[Number.parseInt(String(value), 10) - 1] ?? "";
            },
          },
        ]}
        yAxis={[
          {
            label: "Nombre de demandes",
            max: yAxisMax,
            min: 0,
          },
        ]}
        sx={{
          ".MuiLineElement-root": {
            strokeWidth: 2,
          },
          ".MuiMarkElement-root": {
            stroke: "white",
            scale: "0.6",
            fill: color,
          },
        }}
        slotProps={{
          tooltip: { trigger: "item" },
          legend: {
            position: {
              vertical: "top",
              horizontal: "end",
            },
          },
        }}
      />
    </Box>
  );
}
